/**
 * Stormcaller ability kit: one unique clip (or clip set) per ability. Wand in the right hand, orb in
 * the left (an "empty" hand for trails: the ribbon comes from the fingertips around the orb).
 *
 * Elements, each with its own palette and trail style:
 *   storm  Lightning Bolt, Chain Lightning   electric blue-white, 'electric' crackle
 *   fire   Lava Burst, Flame Shock           lava orange / crimson, 'crescent'
 *   earth  Earthquake                        amber-brown, 'crescent' + ground shockwave
 *   sky    Stormkeeper                       deep storm-blue spiral, 'electric'
 *   wind   Wind Shear                        white-cyan gust, 'streak'
 *   astral Astral Shift                      cosmic violet shimmer, 'holy'
 *   earth  Earth Shock                       amber stomp + uppercut, 'crescent' with an amber crackle
 *   storm  Arc Torrent (channel), Tempest    pale-blue torrent / deep storm-blue, 'electric'
 *
 * Cast-time spells (Lightning Bolt, Lava Burst, Chain Lightning) get a looping `cast` pose that
 * charges while the bar runs and a `strike` that releases on cast success (or on the press when
 * Stormkeeper makes them instant). Everything else is an instant `strike`.
 *
 * Format: see ../clips.ts (offsets on the base pose, radians; root in yards / turns).
 */
import { ABILITY } from '@keystone/content';
import { type Clip, defineClip } from '../clips';
import { registerAbilityClips } from '../registry';

const A = ABILITY.stormcaller;

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------
const BOLT = '#cfeeff'; // blue-white core
const BOLT_TAIL = '#2a6dff';
const CHAIN = '#9fdcff';
const CHAIN_TAIL = '#3b48ff';
const LAVA = '#ff8a1c';
const LAVA_TAIL = '#9e0c12';
const FLAME = '#ff5a1f';
const FLAME_TAIL = '#7a0a14';
const QUAKE = '#ffb347';
const QUAKE_TAIL = '#5a3210';
const STORM = '#3f7bff';
const STORM_TAIL = '#0b1a6e';
const GUST = '#eaffff';
const GUST_TAIL = '#3fd0e0';
const ASTRAL = '#b98cff';
const ASTRAL_TAIL = '#3a1a8a';

// A planted caster stance: feet a little apart, knees soft (used by the cast loops).
const STANCE = {
  leftUpperLeg: [0.12, 0, 0.1],
  rightUpperLeg: [-0.05, 0, 0.1],
  leftLowerLeg: [0.22],
  rightLowerLeg: [0.18],
} as const;

// ---------------------------------------------------------------------------
// Lightning Bolt: hands gather a crackling charge at the chest, then a two-hand forward release.
// ---------------------------------------------------------------------------

const BOLT_CHARGE = {
  spine: [0.02, -0.12, 0],
  chest: [-0.04, -0.06, 0],
  head: [0.05, 0.12, 0],
  rightUpperArm: [0.55, 0.85, 0.55],
  rightLowerArm: [1.15],
  leftUpperArm: [0.55, 0.85, 0.55],
  leftLowerArm: [1.15],
  ...STANCE,
} as const;

export const lightningBoltCast = defineClip({
  id: 'stormcaller.lightning_bolt.cast',
  duration: 0.9,
  loop: true,
  loopFrom: 0.2,
  blendIn: 0.08,
  keys: [
    { t: 0.2, ease: 'out', pose: BOLT_CHARGE },
    // The hands work around the charge ball (the crackle ribbon needs motion to read).
    {
      t: 0.55,
      pose: {
        ...BOLT_CHARGE,
        chest: [-0.07, -0.02, 0],
        rightUpperArm: [0.7, 0.75, 0.5],
        rightLowerArm: [1.3],
        leftUpperArm: [0.42, 0.95, 0.62],
        leftLowerArm: [1.0],
      },
    },
  ],
  trails: [{ from: 0.15, to: 0.9, hand: 'both', color: BOLT, tail: BOLT_TAIL, style: 'electric', width: 0.9, fade: 0.16, alpha: 0.75 }],
  cues: [{ t: 0.55, fx: 'electric', at: 'hand', hand: 'right', scale: 0.35 }],
});

export const lightningBolt = defineClip({
  id: 'stormcaller.lightning_bolt',
  duration: 0.52,
  blendIn: 0.04,
  keys: [
    // Anticipation: rock back, pull the charge to the chest.
    {
      t: 0.09,
      ease: 'out',
      pose: {
        spine: [-0.12, -0.1, 0],
        chest: [-0.08, 0, 0],
        head: [-0.05, 0.1, 0],
        rightUpperArm: [0.45, 0.75, 0.5],
        rightLowerArm: [1.45],
        leftUpperArm: [0.45, 0.75, 0.5],
        leftLowerArm: [1.45],
        rightUpperLeg: [0.15, 0, 0.1],
        rightLowerLeg: [0.35],
        leftLowerLeg: [0.15],
      },
      root: { lunge: -0.06 },
    },
    // Release: both arms snap straight out, deep forward lean onto the front leg.
    {
      t: 0.15,
      ease: 'snap',
      hold: 0.06,
      pose: {
        hips: [0.12, 0, 0],
        spine: [0.36, 0.05, 0],
        chest: [0.1, 0, 0],
        head: [-0.3, 0, 0],
        rightUpperArm: [1.35, 0.55, 0.15],
        rightLowerArm: [-0.05],
        rightHand: [0, 0, -0.5],
        leftUpperArm: [1.35, 0.55, 0.15],
        leftLowerArm: [-0.05],
        leftHand: [0, 0, -0.5],
        leftUpperLeg: [0.5, 0, 0.08],
        leftLowerLeg: [0.45],
        rightUpperLeg: [-0.3, 0, 0.08],
        rightLowerLeg: [0.2],
      },
      root: { lunge: 0.2, hop: -0.03 },
    },
    { t: 0.52, ease: 'out' },
  ],
  trails: [{ from: 0.04, to: 0.25, hand: 'both', color: BOLT, tail: BOLT_TAIL, style: 'electric', width: 1.0, fade: 0.22, alpha: 0.95 }],
  cues: [
    { t: 0.15, fx: 'electric', at: 'front', scale: 1.0 },
    { t: 0.15, fx: 'flash', at: 'hand', hand: 'right', scale: 0.7 },
  ],
  hitStop: { t: 0.15, ms: 30, shake: 0.06 },
});

// ---------------------------------------------------------------------------
// Chain Lightning: arms spread wide (charge arcs between them), then a two-arm sweep across the
// body from right to left with a forked ribbon (a wide crackle plus a thin offset fork per hand).
// ---------------------------------------------------------------------------

const CHAIN_CHARGE = {
  spine: [0.0, -0.3, 0.06],
  chest: [-0.05, -0.12, 0],
  head: [0, 0.35, 0],
  rightUpperArm: [0.35, -0.25, 1.15],
  rightLowerArm: [0.5],
  leftUpperArm: [0.6, 0.7, 0.75],
  leftLowerArm: [0.7],
  ...STANCE,
} as const;

export const chainLightningCast = defineClip({
  id: 'stormcaller.chain_lightning.cast',
  duration: 1.0,
  loop: true,
  loopFrom: 0.22,
  blendIn: 0.1,
  keys: [
    { t: 0.22, ease: 'out', pose: CHAIN_CHARGE },
    {
      t: 0.61,
      pose: {
        ...CHAIN_CHARGE,
        spine: [0.0, -0.36, 0.08],
        rightUpperArm: [0.55, -0.3, 1.25],
        rightLowerArm: [0.35],
        leftUpperArm: [0.5, 0.8, 0.7],
      },
    },
  ],
  trails: [{ from: 0.2, to: 1.0, hand: 'right', color: CHAIN, tail: CHAIN_TAIL, style: 'electric', width: 0.95, fade: 0.18, alpha: 0.75 }],
  cues: [{ t: 0.61, fx: 'electric', at: 'tip', hand: 'right', scale: 0.4 }],
});

export const chainLightning = defineClip({
  id: 'stormcaller.chain_lightning',
  duration: 0.58,
  blendIn: 0.05,
  keys: [
    // Coil hard to the right, both arms swung back out to the right side.
    {
      t: 0.1,
      ease: 'out',
      pose: {
        spine: [0.05, -0.6, 0.05],
        chest: [0, -0.2, 0],
        head: [0, 0.5, 0],
        rightUpperArm: [0.5, -0.45, 1.1],
        rightLowerArm: [0.35],
        leftUpperArm: [0.85, 1.05, 0.4],
        leftLowerArm: [0.55],
        rightUpperLeg: [0.2, 0, 0.12],
        rightLowerLeg: [0.4],
        leftLowerLeg: [0.2],
      },
      root: { side: 0.05, spin: 0.04 },
    },
    // The sweep: torso whips left, both arms rake across the front at chest height.
    {
      t: 0.24,
      ease: 'back',
      overshoot: 1.1,
      hold: 0.05,
      pose: {
        spine: [0.18, 0.65, -0.05],
        chest: [0.05, 0.25, 0],
        head: [-0.1, -0.4, 0],
        rightUpperArm: [1.25, 1.1, 0.3],
        rightLowerArm: [0.1],
        leftUpperArm: [0.55, -0.35, 1.15],
        leftLowerArm: [0.1],
        leftUpperLeg: [0.4, 0, 0.12],
        leftLowerLeg: [0.4],
        rightUpperLeg: [-0.2, 0, 0.1],
        rightLowerLeg: [0.25],
      },
      root: { side: -0.08, lunge: 0.1, spin: -0.04 },
    },
    { t: 0.58, ease: 'out' },
  ],
  trails: [
    { from: 0.05, to: 0.32, hand: 'both', color: CHAIN, tail: CHAIN_TAIL, style: 'electric', width: 1.0, fade: 0.26, alpha: 0.95 },
    // The fork: a thinner, later, fast-fading branch on each hand.
    { from: 0.12, to: 0.3, hand: 'both', color: '#ffffff', tail: CHAIN_TAIL, style: 'streak', width: 0.4, fade: 0.12, alpha: 0.6 },
  ],
  cues: [
    { t: 0.24, fx: 'electric', at: 'tip', hand: 'right', scale: 0.8 },
    { t: 0.25, fx: 'electric', at: 'front', scale: 0.9 },
    { t: 0.24, fx: 'sparks', at: 'hand', hand: 'left', scale: 0.5 },
  ],
  hitStop: { t: 0.24, ms: 40, shake: 0.1 },
});

// ---------------------------------------------------------------------------
// Lava Burst: wand cocked behind the head (molten glow circling), then an overhand throw with a
// step and a big ember arc.
// ---------------------------------------------------------------------------

const LAVA_CHARGE = {
  spine: [-0.06, -0.38, 0],
  chest: [-0.04, -0.15, 0],
  head: [0.05, 0.35, 0],
  rightUpperArm: [2.2, -0.15, 0.75],
  rightLowerArm: [1.25],
  leftUpperArm: [1.1, 0.35, 0.35],
  leftLowerArm: [0.25],
  ...STANCE,
} as const;

export const lavaBurstCast = defineClip({
  id: 'stormcaller.lava_burst.cast',
  duration: 1.0,
  loop: true,
  loopFrom: 0.22,
  blendIn: 0.1,
  keys: [
    { t: 0.22, ease: 'out', pose: LAVA_CHARGE },
    {
      t: 0.61,
      pose: {
        ...LAVA_CHARGE,
        rightUpperArm: [2.35, -0.3, 0.9],
        rightLowerArm: [1.45],
        chest: [-0.07, -0.2, 0],
      },
    },
  ],
  trails: [{ from: 0.2, to: 1.0, hand: 'right', color: LAVA, tail: LAVA_TAIL, style: 'crescent', width: 0.7, fade: 0.16, alpha: 0.45 }],
  cues: [{ t: 0.61, fx: 'embers', at: 'tip', hand: 'right', scale: 0.4 }],
});

export const lavaBurst = defineClip({
  id: 'stormcaller.lava_burst',
  duration: 0.6,
  blendIn: 0.05,
  keys: [
    // Wind-up: deeper coil, wand far back, off arm pointing at the target.
    {
      t: 0.12,
      ease: 'out',
      pose: {
        spine: [-0.15, -0.6, 0.05],
        chest: [-0.08, -0.2, 0],
        head: [0.08, 0.55, 0],
        rightUpperArm: [2.5, -0.4, 0.9],
        rightLowerArm: [1.5],
        leftUpperArm: [1.25, 0.45, 0.3],
        leftLowerArm: [0.1],
        rightUpperLeg: [0.2, 0, 0.12],
        rightLowerLeg: [0.45],
        leftUpperLeg: [-0.1, 0, 0.1],
      },
      root: { lunge: -0.1, hop: -0.03 },
    },
    // The throw: wand whips over the top and down in front; torso unloads to the left.
    {
      t: 0.22,
      ease: 'back',
      overshoot: 1.3,
      hold: 0.06,
      pose: {
        spine: [0.42, 0.45, 0],
        chest: [0.1, 0.15, 0],
        head: [-0.25, -0.3, 0],
        rightUpperArm: [0.8, 0.9, 0.15],
        rightLowerArm: [0.05],
        rightHand: [0, 0, -0.4],
        leftUpperArm: [0.1, -0.3, 0.7],
        leftLowerArm: [0.6],
        leftUpperLeg: [0.6, 0, 0.1],
        leftLowerLeg: [0.6],
        rightUpperLeg: [-0.35, 0, 0.1],
        rightLowerLeg: [0.3],
      },
      root: { lunge: 0.3, hop: -0.05 },
    },
    { t: 0.6, ease: 'out' },
  ],
  trails: [
    { from: 0.06, to: 0.3, hand: 'right', color: LAVA, tail: LAVA_TAIL, style: 'crescent', width: 1.0, fade: 0.26, alpha: 0.9 },
    { from: 0.12, to: 0.3, hand: 'right', color: '#ffd27a', tail: LAVA, style: 'streak', width: 0.45, fade: 0.14, alpha: 0.6 },
  ],
  cues: [
    { t: 0.22, fx: 'embers', at: 'tip', hand: 'right', scale: 1.0 },
    { t: 0.22, fx: 'flash', at: 'front', scale: 0.8 },
    { t: 0.23, fx: 'impact', at: 'front', scale: 0.6 },
  ],
  hitStop: { t: 0.22, ms: 50, shake: 0.15 },
});

// ---------------------------------------------------------------------------
// Earthquake: crouch, leap with both arms overhead, slam both fists into the ground.
// ---------------------------------------------------------------------------

export const earthquake = defineClip({
  id: 'stormcaller.earthquake',
  duration: 0.62,
  blendIn: 0.04,
  // The leap and the landing crouch need the legs even while moving.
  mask: 'full',
  keys: [
    // Load: sink into the knees, arms swing down and back.
    {
      t: 0.1,
      ease: 'out',
      pose: {
        spine: [0.35, 0, 0],
        head: [-0.1, 0, 0],
        rightUpperArm: [-0.45, 0, 0.25],
        rightLowerArm: [0.3],
        leftUpperArm: [-0.45, 0, 0.25],
        leftLowerArm: [0.3],
        leftUpperLeg: [0.55, 0, 0.12],
        rightUpperLeg: [0.55, 0, 0.12],
        leftLowerLeg: [1.0],
        rightLowerLeg: [1.0],
        leftFoot: [-0.4],
        rightFoot: [-0.4],
      },
      root: { hop: -0.1 },
    },
    // Apex: body arched, both arms high overhead, knees tucked.
    {
      t: 0.26,
      ease: 'out',
      pose: {
        spine: [-0.2, 0, 0],
        chest: [-0.1, 0, 0],
        head: [0.1, 0, 0],
        rightUpperArm: [2.6, 0.15, 0.3],
        rightLowerArm: [0.35],
        leftUpperArm: [2.6, 0.15, 0.3],
        leftLowerArm: [0.35],
        leftUpperLeg: [0.6, 0, 0.1],
        rightUpperLeg: [0.25, 0, 0.1],
        leftLowerLeg: [1.1],
        rightLowerLeg: [0.9],
      },
      root: { hop: 0.5, lunge: 0.1 },
    },
    // Slam: drop hard, fold forward, both fists driven down in front.
    {
      t: 0.34,
      ease: 'snap',
      hold: 0.08,
      pose: {
        hips: [0.3, 0, 0],
        spine: [0.6, 0, 0],
        chest: [0.2, 0, 0],
        head: [-0.2, 0, 0],
        rightUpperArm: [0.85, 0.35, 0.15],
        rightLowerArm: [0.15],
        leftUpperArm: [0.85, 0.35, 0.15],
        leftLowerArm: [0.15],
        leftUpperLeg: [0.75, 0, 0.2],
        rightUpperLeg: [0.35, 0, 0.2],
        leftLowerLeg: [1.2],
        rightLowerLeg: [1.35],
        leftFoot: [-0.45],
        rightFoot: [-0.2],
      },
      root: { hop: -0.16, lunge: 0.2 },
    },
    { t: 0.62, ease: 'out' },
  ],
  trails: [{ from: 0.2, to: 0.4, hand: 'both', color: QUAKE, tail: QUAKE_TAIL, style: 'crescent', width: 0.95, fade: 0.22, alpha: 0.85 }],
  cues: [
    { t: 0.34, fx: 'shockwave', at: 'ground', scale: 1.4 },
    { t: 0.34, fx: 'cracks', at: 'ground', scale: 1.1 },
    { t: 0.35, fx: 'dust', at: 'ground', scale: 1.3 },
    { t: 0.34, fx: 'impact', at: 'ground', scale: 0.8 },
  ],
  hitStop: { t: 0.34, ms: 75, shake: 0.32 },
});

// ---------------------------------------------------------------------------
// Flame Shock: a quick wrist flick: a small, sharp orange whip.
// ---------------------------------------------------------------------------

export const flameShock = defineClip({
  id: 'stormcaller.flame_shock',
  duration: 0.4,
  blendIn: 0.03,
  keys: [
    // Cock the wand up by the ear.
    {
      t: 0.07,
      ease: 'out',
      pose: {
        spine: [-0.04, -0.2, 0],
        head: [0, 0.15, 0],
        rightUpperArm: [1.3, 0.2, 0.55],
        rightLowerArm: [1.3],
        rightHand: [0, 0, 0.6],
        leftUpperArm: [0.2, 0.2, 0.2],
        leftLowerArm: [0.4],
      },
    },
    // Flick: elbow and wrist snap out and down.
    {
      t: 0.13,
      ease: 'snap',
      hold: 0.04,
      pose: {
        spine: [0.12, 0.22, 0],
        head: [-0.08, -0.12, 0],
        rightUpperArm: [1.1, 0.7, 0.35],
        rightLowerArm: [0.05],
        rightHand: [0, 0, -0.7],
        leftUpperArm: [0.05, -0.1, 0.35],
        leftLowerArm: [0.5],
        leftUpperLeg: [0.15],
        leftLowerLeg: [0.15],
      },
      root: { lunge: 0.08 },
    },
    { t: 0.4, ease: 'out' },
  ],
  trails: [{ from: 0.04, to: 0.2, hand: 'right', color: FLAME, tail: FLAME_TAIL, style: 'crescent', width: 0.55, fade: 0.15, alpha: 0.85 }],
  cues: [
    { t: 0.13, fx: 'embers', at: 'tip', hand: 'right', scale: 0.6 },
    { t: 0.14, fx: 'flash', at: 'tip', hand: 'right', scale: 0.35 },
  ],
});

// ---------------------------------------------------------------------------
// Stormkeeper: arms flung out, a full rising turn in place (the ribbons draw a storm-blue spiral),
// then both arms thrown up to the sky.
// ---------------------------------------------------------------------------

export const stormkeeper = defineClip({
  id: 'stormcaller.stormkeeper',
  duration: 0.78,
  blendIn: 0.05,
  keys: [
    // Gather: crouch, arms crossed low in front.
    {
      t: 0.1,
      ease: 'out',
      pose: {
        spine: [0.25, 0.2, 0],
        head: [-0.1, -0.1, 0],
        rightUpperArm: [0.5, 0.9, 0.1],
        rightLowerArm: [0.6],
        leftUpperArm: [0.5, 0.9, 0.1],
        leftLowerArm: [0.6],
        leftUpperLeg: [0.35, 0, 0.12],
        rightUpperLeg: [0.35, 0, 0.12],
        leftLowerLeg: [0.65],
        rightLowerLeg: [0.65],
      },
      root: { hop: -0.06, spin: -0.05 },
    },
    // Halfway round: arms out wide at shoulder height, rising.
    {
      t: 0.3,
      ease: 'in',
      pose: {
        spine: [-0.05, 0, 0],
        head: [0.05, 0, 0],
        rightUpperArm: [0.15, -0.1, 1.25],
        rightLowerArm: [0.1],
        leftUpperArm: [0.15, -0.1, 1.25],
        leftLowerArm: [0.1],
        leftUpperLeg: [0.15, 0, 0.08],
        rightUpperLeg: [0.05, 0, 0.08],
        leftLowerLeg: [0.25],
        rightLowerLeg: [0.2],
      },
      root: { hop: 0.06, spin: 0.45 },
    },
    // Full turn, arms climbing (the spiral rises).
    {
      t: 0.46,
      ease: 'linear',
      pose: {
        spine: [-0.15, 0, 0],
        chest: [-0.08, 0, 0],
        head: [0.2, 0, 0],
        rightUpperArm: [0.4, -0.1, 1.8],
        rightLowerArm: [0.1],
        leftUpperArm: [0.4, -0.1, 1.8],
        leftLowerArm: [0.1],
        leftUpperLeg: [0.1, 0, 0.1],
        leftLowerLeg: [0.15],
        rightLowerLeg: [0.15],
      },
      root: { hop: 0.16, spin: 1.0 },
    },
    // Call the storm: both arms straight up, head back. Brief hold.
    {
      t: 0.52,
      ease: 'snap',
      hold: 0.08,
      pose: {
        spine: [-0.22, 0, 0],
        chest: [-0.12, 0, 0],
        head: [0.35, 0, 0],
        rightUpperArm: [2.5, -0.1, 0.45],
        rightLowerArm: [0.05],
        leftUpperArm: [2.5, -0.1, 0.45],
        leftLowerArm: [0.05],
      },
      root: { hop: 0.1, spin: 1.0 },
    },
    // Recover with the spin held on the whole turn (1 ≡ 0): an implicit zero key would unwind it.
    { t: 0.78, ease: 'out', root: { spin: 1.0 } },
  ],
  trails: [
    { from: 0.08, to: 0.56, hand: 'both', color: STORM, tail: STORM_TAIL, style: 'electric', width: 1.0, fade: 0.38, alpha: 0.95 },
    { from: 0.14, to: 0.54, hand: 'both', color: '#5b8cff', tail: STORM_TAIL, style: 'holy', width: 1.0, fade: 0.45, alpha: 0.5 },
  ],
  cues: [
    { t: 0.52, fx: 'electric', at: 'self', scale: 1.1 },
    { t: 0.52, fx: 'flash', at: 'self', scale: 0.9 },
    { t: 0.53, fx: 'shockwave', at: 'ground', color: STORM, scale: 0.6 },
  ],
  hitStop: { t: 0.52, ms: 40, shake: 0.14 },
});

// ---------------------------------------------------------------------------
// Wind Shear: a fast open-palm strike with the orb hand: a white-cyan gust streak.
// ---------------------------------------------------------------------------

export const windShear = defineClip({
  id: 'stormcaller.wind_shear',
  duration: 0.42,
  blendIn: 0.03,
  keys: [
    // Chamber the palm at the hip, shoulder turned in.
    {
      t: 0.06,
      ease: 'out',
      pose: {
        spine: [0, 0.35, 0],
        head: [0, -0.3, 0],
        leftUpperArm: [-0.1, 0.2, 0.5],
        leftLowerArm: [1.4],
        rightUpperArm: [0.3, 0.5, 0.3],
        rightLowerArm: [0.9],
        rightUpperLeg: [0.15],
        rightLowerLeg: [0.3],
      },
      root: { lunge: -0.04 },
    },
    // Strike: palm out, shoulder driven through, step in.
    {
      t: 0.12,
      ease: 'snap',
      hold: 0.05,
      pose: {
        spine: [0.15, -0.4, 0],
        chest: [0.05, -0.15, 0],
        head: [-0.05, 0.4, 0],
        leftUpperArm: [1.4, 0.45, 0.2],
        leftLowerArm: [-0.05],
        leftHand: [0, 0, 0.9],
        rightUpperArm: [-0.2, -0.2, 0.55],
        rightLowerArm: [0.8],
        leftUpperLeg: [0.45, 0, 0.08],
        leftLowerLeg: [0.35],
        rightUpperLeg: [-0.25, 0, 0.08],
        rightLowerLeg: [0.2],
      },
      root: { lunge: 0.18 },
    },
    { t: 0.42, ease: 'out' },
  ],
  trails: [{ from: 0.03, to: 0.2, hand: 'left', color: GUST, tail: GUST_TAIL, style: 'streak', width: 0.9, fade: 0.16, alpha: 0.85 }],
  cues: [
    { t: 0.12, fx: 'flash', at: 'hand', hand: 'left', scale: 0.6 },
    { t: 0.13, fx: 'impact', at: 'front', color: GUST, scale: 0.6 },
  ],
  hitStop: { t: 0.12, ms: 35, shake: 0.08 },
});

// ---------------------------------------------------------------------------
// Astral Shift: drop into a guarded crouch, forearms crossed before the face; violet shimmer.
// ---------------------------------------------------------------------------

export const astralShift = defineClip({
  id: 'stormcaller.astral_shift',
  duration: 0.58,
  blendIn: 0.04,
  keys: [
    // A quick flinch outward (arms flare) so the guard snaps in.
    {
      t: 0.06,
      ease: 'out',
      pose: {
        spine: [-0.08, 0, 0],
        rightUpperArm: [0.4, -0.2, 0.8],
        leftUpperArm: [0.4, -0.2, 0.8],
        rightLowerArm: [0.3],
        leftLowerArm: [0.3],
      },
      root: { hop: 0.03 },
    },
    // Guard: crouched, forearms crossed in front of the face.
    {
      t: 0.16,
      ease: 'snap',
      hold: 0.12,
      pose: {
        spine: [0.3, 0, 0],
        chest: [0.1, 0, 0],
        head: [-0.25, 0, 0],
        rightUpperArm: [1.15, 0.55, 0.2],
        rightLowerArm: [1.55],
        leftUpperArm: [1.05, 0.55, 0.2],
        leftLowerArm: [1.55],
        leftUpperLeg: [0.5, 0, 0.18],
        rightUpperLeg: [0.3, 0, 0.18],
        leftLowerLeg: [0.9],
        rightLowerLeg: [0.95],
        leftFoot: [-0.3],
        rightFoot: [-0.3],
      },
      root: { hop: -0.09, lunge: -0.06 },
    },
    { t: 0.58, ease: 'out' },
  ],
  trails: [{ from: 0.0, to: 0.32, hand: 'both', color: ASTRAL, tail: ASTRAL_TAIL, style: 'holy', width: 1.0, fade: 0.32, alpha: 0.75 }],
  cues: [
    { t: 0.16, fx: 'holy', at: 'self', scale: 1.0 },
    { t: 0.17, fx: 'flash', at: 'self', scale: 0.6 },
  ],
});

// ---------------------------------------------------------------------------
// Rotation rework: Earth Shock (5), Arc Torrent (6, channel) and Tempest (key-1 transform).
// ---------------------------------------------------------------------------

const AMBER = '#ffb347';
const AMBER_TAIL = '#6a3a0c';
const ROCK = '#d98a3a';
const TEMPEST = '#6fb4ff';
const TEMPEST_TAIL = '#1530b0';
const ARC = '#bfe6ff';
const ARC_TAIL = '#2f5cff';

/**
 * Earth Shock: a sumo stomp, then an uppercut. The right knee comes up high with both arms
 * spread, the foot stamps down (amber cracks at the feet), and the orb hand drives up and
 * forward from the hip, flinging the shock at the target. Amber earth, not lava.
 */
export const earthShock = defineClip({
  id: 'stormcaller.earth_shock',
  duration: 0.64,
  blendIn: 0.04,
  mask: 'auto',
  keys: [
    // Lift: right knee high, arms out to the sides for balance, leaning onto the left leg.
    {
      t: 0.12,
      ease: 'out',
      pose: {
        spine: [-0.05, 0, -0.12],
        head: [0, 0, 0.1],
        rightUpperArm: [0.3, -0.2, 1.05],
        rightLowerArm: [0.35],
        leftUpperArm: [0.2, -0.2, 1.0],
        leftLowerArm: [0.6],
        rightUpperLeg: [1.25, 0, 0.25],
        rightLowerLeg: [1.4],
        leftLowerLeg: [0.15],
      },
      root: { hop: 0.05, side: -0.05 },
    },
    // Stamp: the foot comes down hard, deep wide squat, the orb hand cocked low at the hip.
    {
      t: 0.2,
      ease: 'snap',
      pose: {
        spine: [0.3, -0.25, 0],
        chest: [0.08, -0.1, 0],
        head: [-0.2, 0.2, 0],
        rightUpperArm: [0.4, -0.25, 0.75],
        rightLowerArm: [0.9],
        leftUpperArm: [-0.25, 0.1, 0.45],
        leftLowerArm: [1.3],
        leftUpperLeg: [0.45, 0, 0.35],
        rightUpperLeg: [0.45, 0, 0.35],
        leftLowerLeg: [0.85],
        rightLowerLeg: [0.85],
      },
      root: { hop: -0.12 },
    },
    // Uppercut: the orb hand rips up and forward, the torso unwinds into it, rising out of the squat.
    {
      t: 0.3,
      ease: 'back',
      overshoot: 1.2,
      hold: 0.06,
      pose: {
        spine: [0.08, 0.45, 0],
        chest: [-0.04, 0.18, 0],
        head: [-0.12, -0.35, 0],
        leftUpperArm: [1.9, 0.35, 0.2],
        leftLowerArm: [0.25],
        leftHand: [0, 0, -0.6],
        rightUpperArm: [-0.15, -0.2, 0.6],
        rightLowerArm: [0.8],
        leftUpperLeg: [0.45, 0, 0.15],
        leftLowerLeg: [0.4],
        rightUpperLeg: [-0.2, 0, 0.15],
        rightLowerLeg: [0.3],
      },
      root: { lunge: 0.22, hop: 0.02 },
    },
    { t: 0.64, ease: 'out' },
  ],
  trails: [
    { from: 0.18, to: 0.4, hand: 'left', color: AMBER, tail: AMBER_TAIL, style: 'crescent', width: 1, fade: 0.28, alpha: 0.9 },
    { from: 0.2, to: 0.38, hand: 'left', color: '#ffe0a0', tail: ROCK, style: 'electric', width: 0.6, fade: 0.18, alpha: 0.75 },
  ],
  cues: [
    { t: 0.2, fx: 'cracks', at: 'ground', color: ROCK, scale: 0.9 },
    { t: 0.2, fx: 'shockwave', at: 'ground', color: AMBER, scale: 0.9 },
    { t: 0.21, fx: 'dust', at: 'ground', scale: 0.8 },
    { t: 0.3, fx: 'impact', at: 'front', color: AMBER, scale: 1.0 },
    { t: 0.3, fx: 'embers', at: 'hand', hand: 'left', color: AMBER, scale: 0.8 },
    { t: 0.31, fx: 'electric', at: 'front', color: '#ffd27a', scale: 0.6 },
  ],
  hitStop: { t: 0.3, ms: 50, shake: 0.18 },
});

/**
 * Arc Torrent: the press braces into the channel stance (both hands thrust forward, wand and orb
 * side by side) with a crackling flash; the channel loop then holds the hands on the target while
 * the body shudders with each surge (recoil keys) and a continuous electric ribbon pours off both
 * hands.
 */
const ARC_STANCE = {
  hips: [0.08, 0, 0],
  spine: [0.22, 0.05, 0],
  chest: [0.06, 0, 0],
  head: [-0.18, 0, 0],
  rightUpperArm: [1.35, 0.45, 0.15],
  rightLowerArm: [0.1],
  rightHand: [0, 0, -0.4],
  leftUpperArm: [1.3, 0.5, 0.15],
  leftLowerArm: [0.15],
  leftHand: [0, 0, -0.5],
  leftUpperLeg: [0.45, 0, 0.12],
  leftLowerLeg: [0.45],
  rightUpperLeg: [-0.25, 0, 0.12],
  rightLowerLeg: [0.3],
} as const;

export const arcTorrent = defineClip({
  id: 'stormcaller.arc_torrent',
  duration: 0.46,
  blendIn: 0.03,
  keys: [
    { t: 0.06, ease: 'out', pose: { ...BOLT_CHARGE, rightLowerArm: [1.4], leftLowerArm: [1.4] }, root: { lunge: -0.05 } },
    { t: 0.13, ease: 'snap', hold: 0.08, pose: ARC_STANCE, root: { lunge: 0.12, hop: -0.04 } },
    { t: 0.46, ease: 'out' },
  ],
  trails: [{ from: 0.04, to: 0.3, hand: 'both', color: ARC, tail: ARC_TAIL, style: 'electric', width: 1, fade: 0.22, alpha: 0.95 }],
  cues: [
    { t: 0.13, fx: 'flash', at: 'hand', hand: 'right', color: ARC, scale: 0.8 },
    { t: 0.13, fx: 'electric', at: 'front', color: ARC, scale: 1.0 },
  ],
  hitStop: { t: 0.13, ms: 25, shake: 0.06 },
});

export const arcTorrentChannel = defineClip({
  id: 'stormcaller.arc_torrent.channel',
  duration: 0.62,
  loop: true,
  loopFrom: 0.14,
  blendIn: 0.06,
  mask: 'full',
  keys: [
    { t: 0.14, ease: 'out', pose: ARC_STANCE, root: { lunge: 0.1, hop: -0.04 } },
    // Surge: recoil back, arms kick up a touch, then lean back into it.
    {
      t: 0.22,
      ease: 'snap',
      pose: { ...ARC_STANCE, spine: [0.12, 0.08, 0.04], head: [-0.1, 0, 0], rightUpperArm: [1.5, 0.4, 0.2], leftUpperArm: [1.45, 0.45, 0.2], rightLowerArm: [0.25], leftLowerArm: [0.3] },
      root: { lunge: 0.02, hop: -0.03 },
    },
    { t: 0.38, ease: 'inOut', pose: { ...ARC_STANCE, spine: [0.24, 0.02, -0.04], rightUpperArm: [1.32, 0.5, 0.12], leftUpperArm: [1.28, 0.55, 0.12] }, root: { lunge: 0.12, hop: -0.05 } },
    {
      t: 0.46,
      ease: 'snap',
      pose: { ...ARC_STANCE, spine: [0.14, 0.0, -0.03], head: [-0.1, 0, 0], rightUpperArm: [1.48, 0.42, 0.18], leftUpperArm: [1.42, 0.48, 0.18], rightLowerArm: [0.28], leftLowerArm: [0.25] },
      root: { lunge: 0.03, hop: -0.03 },
    },
    // t = 0.62 closes onto the loop start.
  ],
  // One window over the whole loop: an unbroken torrent.
  trails: [
    { from: 0.06, to: 0.62, hand: 'both', color: ARC, tail: ARC_TAIL, style: 'electric', width: 1, fade: 0.2, alpha: 0.95 },
    { from: 0.1, to: 0.62, hand: 'right', color: '#ffffff', tail: CHAIN_TAIL, style: 'streak', width: 0.5, fade: 0.12, alpha: 0.55 },
  ],
  cues: [
    { t: 0.22, fx: 'electric', at: 'front', color: ARC, scale: 0.8 },
    { t: 0.22, fx: 'flash', at: 'hand', hand: 'right', color: ARC, scale: 0.45 },
    { t: 0.46, fx: 'electric', at: 'hand', hand: 'left', color: CHAIN, scale: 0.55 },
    { t: 0.46, fx: 'sparks', at: 'front', color: ARC, scale: 0.5 },
  ],
});

/**
 * Tempest (1.5 s cast, Lightning Bolt's transform): the storm gathers overhead. The cast loop
 * holds the wand high and circles it (a ring of crackle above the head) with the orb hand
 * reaching up beside it; the release hauls the storm down: a hop, then both arms slammed
 * forward-down at the target with a stamp. Deeper, heavier blue than Lightning Bolt.
 */
const TEMPEST_RAISE = {
  spine: [-0.14, 0.05, 0],
  chest: [-0.08, 0, 0],
  head: [-0.3, 0, 0],
  rightUpperArm: [2.55, 0.05, 0.35],
  rightLowerArm: [0.3],
  leftUpperArm: [2.2, 0.25, 0.6],
  leftLowerArm: [0.35],
  ...STANCE,
} as const;

export const tempestCast = defineClip({
  id: 'stormcaller.tempest.cast',
  duration: 1.0,
  loop: true,
  loopFrom: 0.2,
  blendIn: 0.08,
  keys: [
    { t: 0.2, ease: 'out', pose: TEMPEST_RAISE, root: { hop: 0.02 } },
    // The wand circles overhead: arm sweep and raise a quarter cycle apart.
    { t: 0.4, pose: { ...TEMPEST_RAISE, rightUpperArm: [2.4, 0.35, 0.45], spine: [-0.14, 0.12, 0] }, root: { hop: 0.03 } },
    { t: 0.6, pose: { ...TEMPEST_RAISE, rightUpperArm: [2.7, 0.05, 0.6] }, root: { hop: 0.02 } },
    { t: 0.8, pose: { ...TEMPEST_RAISE, rightUpperArm: [2.45, -0.3, 0.4], spine: [-0.14, -0.04, 0] }, root: { hop: 0.03 } },
  ],
  trails: [
    { from: 0.15, to: 1.0, hand: 'right', color: TEMPEST, tail: TEMPEST_TAIL, style: 'electric', width: 1, fade: 0.3, alpha: 0.85 },
    { from: 0.2, to: 1.0, hand: 'left', color: BOLT, tail: BOLT_TAIL, style: 'electric', width: 0.8, fade: 0.14, alpha: 0.6 },
  ],
  cues: [
    { t: 0.2, fx: 'electric', at: 'tip', hand: 'right', color: TEMPEST, scale: 0.5 },
    { t: 0.6, fx: 'electric', at: 'tip', hand: 'right', color: BOLT, scale: 0.45 },
  ],
});

export const tempest = defineClip({
  id: 'stormcaller.tempest',
  duration: 0.66,
  blendIn: 0.04,
  keys: [
    // Haul: both arms straight up, a hop, chest arched.
    {
      t: 0.12,
      ease: 'out',
      pose: {
        spine: [-0.24, 0, 0],
        chest: [-0.12, 0, 0],
        head: [-0.35, 0, 0],
        rightUpperArm: [2.8, 0.05, 0.3],
        rightLowerArm: [0.1],
        leftUpperArm: [2.8, 0.05, 0.3],
        leftLowerArm: [0.1],
        leftFoot: [-0.3],
        rightFoot: [-0.3],
      },
      root: { hop: 0.18, lunge: -0.06 },
    },
    // Bring it down: both arms slammed forward-down at the target, deep lunge, stamp.
    {
      t: 0.22,
      ease: 'snap',
      hold: 0.08,
      pose: {
        hips: [0.1, 0, 0],
        spine: [0.48, 0, 0],
        chest: [0.12, 0, 0],
        head: [-0.32, 0, 0],
        rightUpperArm: [1.05, 0.45, 0.1],
        rightLowerArm: [0.02],
        rightHand: [0, 0, -0.5],
        leftUpperArm: [1.0, 0.5, 0.1],
        leftLowerArm: [0.05],
        leftHand: [0, 0, -0.5],
        leftUpperLeg: [0.65, 0, 0.1],
        leftLowerLeg: [0.7],
        rightUpperLeg: [-0.3, 0, 0.1],
        rightLowerLeg: [0.45],
      },
      root: { lunge: 0.3, hop: -0.12 },
    },
    { t: 0.66, ease: 'out' },
  ],
  trails: [
    { from: 0.04, to: 0.32, hand: 'both', color: TEMPEST, tail: TEMPEST_TAIL, style: 'electric', width: 1, fade: 0.28, alpha: 0.95 },
    { from: 0.12, to: 0.3, hand: 'both', color: BOLT, tail: BOLT_TAIL, style: 'streak', width: 0.5, fade: 0.14, alpha: 0.6 },
  ],
  cues: [
    { t: 0.12, fx: 'electric', at: 'self', color: TEMPEST, scale: 0.8 },
    { t: 0.22, fx: 'electric', at: 'front', color: TEMPEST, scale: 1.3 },
    { t: 0.22, fx: 'flash', at: 'front', color: BOLT, scale: 0.9 },
    { t: 0.23, fx: 'shockwave', at: 'ground', color: TEMPEST, scale: 1.0 },
    { t: 0.24, fx: 'dust', at: 'ground', scale: 0.6 },
  ],
  hitStop: { t: 0.22, ms: 50, shake: 0.18 },
});

/** Every stormcaller clip (tests and the debug browser). */
export const STORMCALLER_CLIPS: readonly Clip[] = [
  lightningBoltCast,
  lightningBolt,
  chainLightningCast,
  chainLightning,
  lavaBurstCast,
  lavaBurst,
  earthquake,
  flameShock,
  stormkeeper,
  windShear,
  astralShift,
  earthShock,
  arcTorrent,
  arcTorrentChannel,
  tempestCast,
  tempest,
];

export function registerStormcallerKit(): void {
  registerAbilityClips(A.lightningBolt, { cast: lightningBoltCast, strike: lightningBolt });
  registerAbilityClips(A.chainLightning, { cast: chainLightningCast, strike: chainLightning });
  registerAbilityClips(A.lavaBurst, { cast: lavaBurstCast, strike: lavaBurst });
  registerAbilityClips(A.earthquake, { strike: earthquake });
  registerAbilityClips(A.flameShock, { strike: flameShock });
  registerAbilityClips(A.stormkeeper, { strike: stormkeeper });
  registerAbilityClips(A.windShear, { strike: windShear });
  registerAbilityClips(A.astralShift, { strike: astralShift });
  registerAbilityClips(A.earthShock, { strike: earthShock });
  registerAbilityClips(A.arcTorrent, { strike: arcTorrent, channel: arcTorrentChannel });
  registerAbilityClips(A.tempest, { cast: tempestCast, strike: tempest });
}
